import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Button } from '@ui-library/button';
import { Popover } from '@ui-library/popover';
import { SmallText } from '@ui-library/typography';
import { Flex } from '@ui-library/flex';
import type { TrialType } from '../types';
import { formatDaysRemaining } from '../utils';

const PopoverContent = styled(Flex)`
  max-width: 240px;
`;

const getCtaLabel = (trialType?: TrialType): string => {
  if (trialType === 'addon') {
    return 'Buy Add-on';
  }
  return 'Upgrade';
};

const getDaysLabel = (daysRemaining: number): string => {
  if (daysRemaining <= 0) {
    return 'Your trial has expired.';
  }
  return `${formatDaysRemaining(daysRemaining)} remaining in your trial.`;
};

interface TrialPopoverProps {
  title: string;
  description: string;
  daysRemaining?: number;
  trialType?: TrialType;
  onAction?: () => void;
  actionLabel?: string;
  children: React.ReactElement;
}

/**
 * Shared popover for trial-related badges.
 * Shows a title, description with optional days remaining, and an action button.
 */
export const TrialPopover: React.FC<TrialPopoverProps> = ({
  title,
  description,
  daysRemaining,
  trialType,
  onAction,
  actionLabel,
  children,
}) => {
  const { token } = useTheme();
  const daysText = daysRemaining !== undefined ? ` ${getDaysLabel(daysRemaining)}` : '';
  const ctaLabel = actionLabel ?? getCtaLabel(trialType);

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      content={
        <PopoverContent flexDirection="column" gap="0.5rem">
          <Flex flexDirection="column" gap="0.25rem">
            <SmallText fontWeight={600}>{title}</SmallText>
            <SmallText color={token.colorTextDescription}>
              {description}
              {daysText}
            </SmallText>
          </Flex>
          {onAction && (
            <Button size="small" type="primary" onClick={onAction}>
              {ctaLabel}
            </Button>
          )}
        </PopoverContent>
      }
    >
      <Flex inline className="cursor-pointer">
        {children}
      </Flex>
    </Popover>
  );
};
